'use client';

/**
 * EditUserRoleModal Component
 *
 * Modal for changing a user's role within an organization.
 * Updates the user's permissions and access level.
 */

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Inline,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
} from '@cortex/ui';
import { Shield } from 'lucide-react';
import * as React from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

interface EditUserRoleModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** User to edit */
  user: {
    id: string;
    email: string;
    full_name: string | null;
    role: 'user' | 'admin';
  };
  /** Callback when user role is successfully updated */
  onSuccess?: () => void;
}

export function EditUserRoleModal({
  open,
  onClose,
  user,
  onSuccess,
}: EditUserRoleModalProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [role, setRole] = React.useState<'user' | 'admin'>(user.role);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setRole(user.role);
      setError(null);
      setLoading(false);
    }
  }, [open, user.role]);

  const handleSubmit = async () => {
    if (role === user.role) {
      onClose();
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await fetchWithAuth(`/admin/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update user role');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const hasChanged = role !== user.role;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit User Role</DialogTitle>
          <DialogDescription>
            Change the user&apos;s role and permissions
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Stack gap="md">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Card className="bg-surface-2 p-4">
              <Inline gap="sm" align="start">
                <Stack className="bg-surface-3 flex h-10 w-10 items-center justify-center rounded-full">
                  <Shield className="text-foreground/70 h-5 w-5" />
                </Stack>
                <Stack className="flex-1" gap="none">
                  <Text weight="medium">
                    {user.full_name || user.email.split('@')[0]}
                  </Text>
                  <Text size="sm" color="muted">
                    {user.email}
                  </Text>
                  <Badge variant="secondary" className="mt-1">
                    Current:{' '}
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </Badge>
                </Stack>
              </Inline>
            </Card>

            <Stack gap="xs">
              <Label htmlFor="userRole">New Role</Label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as 'user' | 'admin')}
                disabled={loading}
              >
                <SelectTrigger id="userRole">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    <Stack gap="none">
                      <Text weight="medium">User</Text>
                      <Text className="text-foreground/60 text-sm">
                        Can view and create projects
                      </Text>
                    </Stack>
                  </SelectItem>
                  <SelectItem value="admin">
                    <Stack gap="none">
                      <Text weight="medium">Admin</Text>
                      <Text className="text-foreground/60 text-sm">
                        Full organization access
                      </Text>
                    </Stack>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Text color="muted" size="xs" className="mt-1">
                Changes will take effect immediately
              </Text>
            </Stack>

            {hasChanged && (
              <Alert variant="warning">
                <AlertDescription>
                  Changing the role will immediately update the user&apos;s
                  permissions and access level.
                </AlertDescription>
              </Alert>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!hasChanged}
            loading={loading}
          >
            Update Role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
